import type { RolNumero, RoutingConfig } from '@/api/notification-routing.api';
import type { MessagesConfig } from '@/api/installation.api';
import { MESSAGE_TEMPLATE_CATALOG, type TemplateSeccion } from './message-templates';

/**
 * A QUIÉN LE LLEGA CADA AVISO — lógica PURA (sin React) de la pantalla única
 * de avisos (Mi negocio → Mensajes) y del resumen de /settings → Avisos.
 *
 * Es el espejo en el panel de la cadena de destino del servidor
 * (notification-routing.service + owner-alerts.service):
 *
 *   números marcados a mano → números por rol → número del dueño → NADA
 *
 * Vive acá y no dentro del componente porque es lo que el dueño lee para
 * decidir: si la pantalla dijera un destino y el servidor mandara a otro, el
 * dueño creería que un aviso sale cuando en realidad no sale.
 */

/** Nombre humano de cada rol del directorio de números. */
export const ROL_LABELS: Record<RolNumero, string> = {
  dueno: 'Dueño',
  secretaria: 'Secretaria',
  encargado_tecnicos: 'Encargado de técnicos',
  otro: 'Otro',
};

/**
 * Avisos que ya salen dirigidos sin marcar nada — ESPEJO EXACTO de
 * `ROLES_POR_DEFECTO` en apps/api/src/services/notification-routing.service.ts.
 *
 * La nota de la pantalla se arma de acá (antes estaba escrita a mano y decía
 * "tres" cuando el servidor ya ruteaba cinco).
 */
export const ROLES_POR_DEFECTO: Partial<Record<string, RolNumero[]>> = {
  garantia_reincidente: ['dueno', 'encargado_tecnicos'],
  garantia_reincidente_instalacion: ['dueno', 'encargado_tecnicos'],
  resumen_diario_trabajos: ['dueno', 'encargado_tecnicos'],
  cierre_fuera_de_radio: ['dueno', 'encargado_tecnicos'],
  sla_vencido: ['dueno', 'encargado_tecnicos'],
};

/**
 * Los dos avisos internos del sistema que NO tienen texto editable: su switch
 * y sus destinos son lo único que el dueño puede tocar.
 */
const AVISOS_SIN_PLANTILLA: { trigger: string; titulo: string; cuando: string }[] = [
  {
    trigger: 'reporte_diario',
    titulo: 'Reporte diario 8AM',
    cuando: 'Resumen de la operación que sale cada mañana. Lo arma el sistema solo.',
  },
  {
    trigger: 'alerta_guardian',
    titulo: 'Alertas del guardián (canal caído, backup, disco, cupo)',
    cuando: 'Avisa cuando algo del sistema se rompe: WhatsApp caído, disco lleno, backup fallido.',
  },
];

export interface AvisoPreRuteado {
  trigger: string;
  titulo: string;
  roles: RolNumero[];
}

/** Los avisos que vienen dirigidos de fábrica, con su título humano. */
export function avisosPreRuteados(): AvisoPreRuteado[] {
  return Object.entries(ROLES_POR_DEFECTO).map(([trigger, roles]) => ({
    trigger,
    titulo: tituloDeAviso(trigger),
    roles: roles ?? [],
  }));
}

/** "Dueño y Encargado de técnicos": los roles de los pre-ruteados, en palabras. */
export function rolesPreRuteadosEnPalabras(): string {
  const roles = new Set<RolNumero>();
  for (const lista of Object.values(ROLES_POR_DEFECTO)) for (const r of lista ?? []) roles.add(r);
  const nombres = [...roles].map((r) => ROL_LABELS[r]);
  if (nombres.length <= 1) return nombres.join('');
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/** Una fila de la pantalla de avisos: título + switch (+ destinos si es interno). */
export interface FilaAviso {
  trigger: string;
  titulo: string;
  /** Cuándo se envía, en una línea. */
  cuando: string;
  seccion: TemplateSeccion;
  /** Plantilla cuyo texto edita este aviso; `null` = no tiene texto editable. */
  plantillaKey: keyof MessagesConfig | null;
  /** Solo los internos eligen números; cliente y técnico ya tienen destino fijo. */
  ruteable: boolean;
}

/**
 * Las filas de la pantalla, UNA por switch. Las plantillas que comparten
 * disparador (las tres respuestas de encuesta) son una sola fila: un switch,
 * una fila. Las obligatorias (baja y re-alta) no aparecen: no se apagan.
 */
export function filasDeAvisos(): FilaAviso[] {
  const porTrigger = new Map<string, FilaAviso>();
  for (const t of MESSAGE_TEMPLATE_CATALOG) {
    if (t.trigger === null || porTrigger.has(t.trigger)) continue;
    porTrigger.set(t.trigger, {
      trigger: t.trigger,
      titulo: t.titulo,
      cuando: t.cuando,
      seccion: t.seccion,
      plantillaKey: t.key,
      ruteable: t.seccion === 'interno',
    });
  }
  for (const a of AVISOS_SIN_PLANTILLA) {
    if (porTrigger.has(a.trigger)) continue;
    porTrigger.set(a.trigger, { ...a, seccion: 'interno', plantillaKey: null, ruteable: true });
  }
  const filas = [...porTrigger.values()];
  const orden: TemplateSeccion[] = ['interno', 'cliente', 'tecnico'];
  return filas.sort((a, b) => orden.indexOf(a.seccion) - orden.indexOf(b.seccion));
}

/** Título humano de un disparador. Si no se conoce, devuelve la clave cruda. */
export function tituloDeAviso(trigger: string): string {
  const conPlantilla = MESSAGE_TEMPLATE_CATALOG.find((t) => t.trigger === trigger);
  if (conPlantilla) return conPlantilla.titulo;
  return AVISOS_SIN_PLANTILLA.find((a) => a.trigger === trigger)?.titulo ?? trigger;
}

/** Sin tildes y en minúsculas: nadie escribe "garantías" con tilde al buscar. */
function normalizar(texto: string): string {
  return texto.toLowerCase().normalize('NFD').replace(/\p{M}/gu, '');
}

/** El buscador "Buscar aviso": mira el título y el cuándo se envía. */
export function buscarFilas(filas: FilaAviso[], texto: string): FilaAviso[] {
  const q = normalizar(texto.trim());
  if (!q) return filas;
  return filas.filter((f) => normalizar(`${f.titulo} ${f.cuando}`).includes(q));
}

/** A dónde va HOY un aviso, tal como lo resolvería el servidor. */
export type Destino =
  | { tipo: 'numeros'; nombres: string[] }
  | { tipo: 'rol'; nombres: string[]; roles: RolNumero[] }
  | { tipo: 'respaldo'; phone: string }
  | { tipo: 'sin-destino' };

export function destinoDeAviso(
  routing: RoutingConfig,
  trigger: string,
  ownerPhone: string,
): Destino {
  const elegidos = routing.rutas[trigger]?.numeroIds ?? [];
  const respaldo: Destino = ownerPhone
    ? { tipo: 'respaldo', phone: ownerPhone }
    : { tipo: 'sin-destino' };

  // Lo que el dueño eligió a mano MANDA, incluso si algún id quedó huérfano
  // (número borrado): el servidor tampoco cae al default por rol en ese caso —
  // sería mandarle el aviso justo a quien él sacó de la lista.
  if (elegidos.length > 0) {
    const nombres = elegidos
      .map((id) => routing.numeros.find((n) => n.id === id)?.nombre)
      .filter((n): n is string => Boolean(n));
    return nombres.length > 0 ? { tipo: 'numeros', nombres } : respaldo;
  }

  const roles = ROLES_POR_DEFECTO[trigger];
  if (roles) {
    const nombres = routing.numeros.filter((n) => roles.includes(n.rol)).map((n) => n.nombre);
    if (nombres.length > 0) return { tipo: 'rol', nombres, roles };
  }

  return respaldo;
}

/** Los avisos donde ESTE número está marcado a mano (antes de borrarlo). */
export function avisosQueUsanNumero(routing: RoutingConfig, numeroId: string): string[] {
  return Object.entries(routing.rutas)
    .filter(([, ruta]) => ruta.numeroIds.includes(numeroId))
    .map(([trigger]) => tituloDeAviso(trigger));
}

/** Cuántos avisos internos tienen números propios (resumen de /settings → Avisos). */
export function resumenDeRutas(routing: RoutingConfig): {
  conNumerosPropios: number;
  total: number;
} {
  const ruteables = filasDeAvisos().filter((f) => f.ruteable);
  const conNumerosPropios = ruteables.filter(
    (f) => (routing.rutas[f.trigger]?.numeroIds ?? []).length > 0,
  ).length;
  return { conNumerosPropios, total: ruteables.length };
}
